// ----------------------------------------------------------------------

export const ROOTS = {
    AUTH: "/auth",
    DASHBOARD: "",
    LOGGA_IN: "/logga-in",
    PREMIUM: "/premium",
    VERIFIERA: "/verifiera",
    REGISTRERA: "/registrera",
    ÅTERSTÄLL: "/aterstall",
    ONBOARDING: "/onboarding",
    // OBS: /verifiera är upptaget av e-postverifieringen — BankID ligger under /legitimering.
    LEGITIMERING: "/legitimering",
    EKONOMI: "/ekonomi",
    BOKFÖR: "/bokforing",
    OFFERT: "/offert",
    PRESENTATION: "/presentation"
};

// ----------------------------------------------------------------------

export const paths = {
    faqs: "/faqs",
    PREMIUM: ROOTS.PREMIUM,
    LOGGA_IN: ROOTS.LOGGA_IN,
    VERIFIERA: ROOTS.VERIFIERA,
    REGISTRERA: ROOTS.REGISTRERA,
    ÅTERSTÄLL: ROOTS.ÅTERSTÄLL,
    ONBOARDING: ROOTS.ONBOARDING,
    LEGITIMERING_CALLBACK: `${ROOTS.LEGITIMERING}/callback`,
    // AUTH
    authAction: '/auth/action',
    fel: '/fel',
    rapporteraProblem: '/rapportera-ett-problem',
    arsredovisningPublik: (slug: string) => `/arsredovisning/${slug}`,
    inbjudan: (inbjudanId: string) => `/inbjudan/${inbjudanId}`,
    /**
     * Hantverkarens personliga offertlänk. Ligger utanför (dashboard) och bakom ingen AuthGuard —
     * hantverkaren har inget konto, och token i sökvägen är hela behörigheten.
     */
    hantverkarOffert: (token: string) => `/hantverkare/offert/${token}`,
    /**
     * Publika presentationer. Ligger utanför (dashboard) och omfattas därför varken av
     * AuthGuard eller DashboardLayout. Nycklarna bär å/ä/ö, URL-segmenten är ren ASCII.
     */
    presentation: {
        root: ROOTS.PRESENTATION,
        autopilot: `${ROOTS.PRESENTATION}/autopilot`,
        händelser: `${ROOTS.PRESENTATION}/handelser`,
        bank: `${ROOTS.PRESENTATION}/bank`,
        styrelsen: `${ROOTS.PRESENTATION}/styrelsen`,
        mittHem: `${ROOTS.PRESENTATION}/mitt-hem`
    },
    // DASHBOARD
    dashboard: {
        root: "/",
        demo: `${ROOTS.DASHBOARD}/demo`,
        two: `${ROOTS.DASHBOARD}/two`,
        three: `${ROOTS.DASHBOARD}/three`,
        group: {
            root: `${ROOTS.DASHBOARD}/group`,
            five: `${ROOTS.DASHBOARD}/group/five`,
            six: `${ROOTS.DASHBOARD}/group/six`,
        },

        arende: {
            root: `${ROOTS.DASHBOARD}/arende`,
            nytt: `${ROOTS.DASHBOARD}/arende/nytt`,
            boende: `${ROOTS.DASHBOARD}/arende/boende`,
            styrelse: `${ROOTS.DASHBOARD}/arende/styrelse`,
            details: (id: string) => `${ROOTS.DASHBOARD}/arende/${id}`,
            edit: (id: string) => `${ROOTS.DASHBOARD}/arende/${id}/edit`,
        },
        user: {
            root: `${ROOTS.DASHBOARD}/user`,
            edit: `${ROOTS.DASHBOARD}/user/redigera`,
        },
        kontoinställningar: `${ROOTS.DASHBOARD}/kontoinstallningar`,
        boende: {
            root: `${ROOTS.DASHBOARD}/boende`,
            värme: `${ROOTS.DASHBOARD}/boende/varme`,
            bokaTvattstuga: `${ROOTS.DASHBOARD}/boende/boka-tvattstuga`,
            garage: `${ROOTS.DASHBOARD}/boende/garage`,
            // Fordonspoolen. Nycklarna behåller å/ä/ö, patharna är ren ASCII — samma delning
            // som värme ovan.
            bilpool: `${ROOTS.DASHBOARD}/boende/bilpool`,
            lådcykelpool: `${ROOTS.DASHBOARD}/boende/ladcykelpool`,
            släp: `${ROOTS.DASHBOARD}/boende/slap`,
            // Samåkning hör till fordonsgrenen även om ingen bil ägs av föreningen —
            // det är samma fråga för den boende: hur tar jag mig dit.
            samåkning: {
                root: `${ROOTS.DASHBOARD}/boende/samakning`,
                minaResor: `${ROOTS.DASHBOARD}/boende/samakning/mina-resor`,
                detalj: (id: string) => `${ROOTS.DASHBOARD}/boende/samakning/${id}`,
            },
            kanaler: `${ROOTS.DASHBOARD}/boende/kanaler`,
            new: `${ROOTS.DASHBOARD}/boende/new`,
            list: `${ROOTS.DASHBOARD}/boende/list`,
            utlåningspool: {
                root: `${ROOTS.DASHBOARD}/boende/utlaningspool`,
                importera: `${ROOTS.DASHBOARD}/boende/utlaningspool/importera`,
                Inköpsförslag: `${ROOTS.DASHBOARD}/boende/utlaningspool/inkopsforslag`,
                minaBokningar: `${ROOTS.DASHBOARD}/boende/utlaningspool/mina-bokningar`,
                admin: `${ROOTS.DASHBOARD}/boende/utlaningspool/admin`,
                detalj: (id: string) => `${ROOTS.DASHBOARD}/boende/utlaningspool/${id}`,
                bokningsbekräftelse: (bokningId: string) =>
                    `${ROOTS.DASHBOARD}/boende/utlaningspool/bokning/${bokningId}`,
            },
            cards: `${ROOTS.DASHBOARD}/boende/cards`,
            profile: `${ROOTS.DASHBOARD}/boende/profile`,
            account: `${ROOTS.DASHBOARD}/boende/account`,
            edit: (id: string) => `${ROOTS.DASHBOARD}/boende/${id}/edit`,
        },
        facebookGrupp: `${ROOTS.DASHBOARD}/facebookgrupp`,
        order: {
            root: `${ROOTS.DASHBOARD}/order`,
            details: (id: string) => `${ROOTS.DASHBOARD}/order/${id}`,
        },
        styrelse: {
            root: `${ROOTS.DASHBOARD}/styrelse`,
            nastaMote: `${ROOTS.DASHBOARD}/styrelse/nasta-mote`,
            ledamoter: `${ROOTS.DASHBOARD}/styrelse/sammansattning`,
            arkiv: {
                root: `${ROOTS.DASHBOARD}/styrelse/arkiv`,
                detalj: (id: string) => `${ROOTS.DASHBOARD}/styrelse/arkiv/${id}`,
            },
        },
        ekonomi: {
            root: `${ROOTS.EKONOMI}`,
            bank: {
                root: `${ROOTS.EKONOMI}/bank`,
                callback: `${ROOTS.EKONOMI}/bank/callback`,
                transaktionskonto: {
                    root: `${ROOTS.EKONOMI}/bank/transaktionskonto`,
                },
                konton: {
                    root: `${ROOTS.EKONOMI}/bank/konton`,
                },
            },
            bokforing: {
                root: `${ROOTS.EKONOMI}/bokforing`,
                bokfor: `${ROOTS.EKONOMI}/bokforing/bokfor`,
                händelser: {
                    root: `${ROOTS.EKONOMI}/bokforing/handelser/`,
                    verifikationer: `${ROOTS.EKONOMI}/bokforing/handelser/verifikationer`,
                    utkast: {
                        root: `${ROOTS.EKONOMI}/bokforing/utkast/`,
                        visa: (id: string) => `${ROOTS.EKONOMI}/bokforing/utkast/${id}`,
                    },
                    visa: (id: string) => `${ROOTS.EKONOMI}/bokforing/handelser/${id}`,
                },
                verifikationer: {
                    root: `${ROOTS.EKONOMI}/bokforing/verifikationer`,
                    visa: (id: string) => `${ROOTS.EKONOMI}/bokforing/verifikationer/${id}`,
                },
                resultaträkning: `${ROOTS.EKONOMI}/bokforing/resultatrakning`,
                balansräkning: `${ROOTS.EKONOMI}/bokforing/balansrakning`,
                huvudbok: `${ROOTS.EKONOMI}/bokforing/huvudbok`,
                grundbok: `${ROOTS.EKONOMI}/bokforing/grundbok`,
                exportera: `${ROOTS.EKONOMI}/bokforing/exportera`,
                bokslut: {
                    root: `${ROOTS.EKONOMI}/bokforing/bokslut`,
                    importera: `${ROOTS.EKONOMI}/bokforing/importera`,
                    visa: (id: string) => `${ROOTS.EKONOMI}/bokforing/bokslut/${id}`,
                },
                admin: {
                    root: `${ROOTS.EKONOMI}/bokforing/admin`,
                },
            },
            arsredovisningar: {
                root: `${ROOTS.EKONOMI}/arsredovisning`,
                senaste: `${ROOTS.EKONOMI}/arsredovisning`,
                arkiv: `${ROOTS.EKONOMI}/arsredovisning/arkiv`,
                sokBolagsverket: `${ROOTS.EKONOMI}/arsredovisning/sok`,
                utkast: `${ROOTS.EKONOMI}/arsredovisning/utkast`,
                visa: (slug: string) => `${ROOTS.EKONOMI}/arsredovisning/${slug}`,
            },
            fakturor: {
                root: `${ROOTS.EKONOMI}/fakturor`,
                kundfakturor: `${ROOTS.EKONOMI}/fakturor/kundfakturor`,
                levfakturor: `${ROOTS.EKONOMI}/fakturor/levfakturor`,
                händelser: {
                    root: `${ROOTS.EKONOMI}/bokforing/handelser/`,
                    verifikationer: `${ROOTS.EKONOMI}/bokforing/handelser/verifikationer`,
                    utkast: {
                        root: `${ROOTS.EKONOMI}/bokforing/utkast/`,
                        visa: (id: string) => `${ROOTS.EKONOMI}/bokforing/utkast/${id}`,
                    },
                    visa: (id: string) => `${ROOTS.EKONOMI}/bokforing/handelser/${id}`,
                },
                verifikationer: {
                    root: `${ROOTS.EKONOMI}/bokforing/verifikationer`,
                    visa: (id: string) => `${ROOTS.EKONOMI}/bokforing/verifikationer/${id}`,
                },
                bokslut: {
                    root: `${ROOTS.EKONOMI}/bokforing/bokslut`,
                    visa: (id: string) => `${ROOTS.EKONOMI}/bokforing/bokslut/${id}`,
                },
            },
            moms: {
                root: `${ROOTS.EKONOMI}/moms`,
                ny: `${ROOTS.EKONOMI}/moms/ny-momsdeklaration`,
                momsdeklarationer: {
                    root: `${ROOTS.EKONOMI}/moms/momsdeklarationer`,
                    visa: (id: string) => `${ROOTS.EKONOMI}/moms/momsdeklarationer/${id}`,
                },
            },
            skatter: {
                root: `${ROOTS.EKONOMI}/skatt`,
                transaktioner: {
                    root: `${ROOTS.EKONOMI}/skatt/transaktioner`,
                    visa: (id: string) => `${ROOTS.EKONOMI}/skatt/transaktioner/${id}`,
                },
            },
            loner: {
                root: `${ROOTS.EKONOMI}/loner`,
                ny: `${ROOTS.EKONOMI}/loner/ny`,
                kommande: `${ROOTS.EKONOMI}/loner/kommande`,
                utbetalda: `${ROOTS.EKONOMI}/loner/utbetalda`,
            },
            forsakringar: {
                root: `${ROOTS.EKONOMI}/forsakringar`,
            },
            kapitaltillskott: `${ROOTS.EKONOMI}/kapitaltillskott`,
            fastighetslan: {
                root: `${ROOTS.EKONOMI}/fastighetslan`,
                v2: `${ROOTS.EKONOMI}/fastighetslan/v2`,
            },
            budget: {
                root: `${ROOTS.EKONOMI}/budget`,
                visa: (id: string) => `${ROOTS.EKONOMI}/budget/${id}`,
                // Flikarna ligger i query, så en djuplänk till likviditeten inte kräver
                // en egen sida. Nyckeln är ren ASCII enligt regeln om URL:er.
                flik: (id: string, flik: 'budget' | 'historik') =>
                    `${ROOTS.EKONOMI}/budget/${id}?flik=${flik}`,
            },
            mottagnaEmail: `${ROOTS.EKONOMI}/mottagna-email`,
            emailInställningar: `${ROOTS.EKONOMI}/email-installningar`,
        },
        konsult: {
            root: `${ROOTS.DASHBOARD}/konsult`,
            översikt: `${ROOTS.DASHBOARD}/konsult/oversikt`,
            affärshändelser: `${ROOTS.DASHBOARD}/konsult/bokforing-ai/affarshandelser`,
        },
        utskick: {
            root: `${ROOTS.DASHBOARD}/utskick`,
            skapa: `${ROOTS.DASHBOARD}/utskick/skapa`,
            skapaV2: `${ROOTS.DASHBOARD}/utskick/skapa-v2`,
            förhandsgranska: `${ROOTS.DASHBOARD}/utskick/skapa/forhandsgranska`,
            schemalagda: `${ROOTS.DASHBOARD}/utskick/schemalagda`,
            arkiv: `${ROOTS.DASHBOARD}/utskick/arkiv`
        },
        organisation_users: `${ROOTS.DASHBOARD}/organisation-users`,
        inkorg: `${ROOTS.DASHBOARD}/inkorg`,
        notifieringar: `${ROOTS.DASHBOARD}/notifieringar`,
        meddelanden: {
            root: `${ROOTS.DASHBOARD}/meddelanden`,
            tråd: (id: string) => `${ROOTS.DASHBOARD}/meddelanden/${id}`,
        },
        organisation: {
            root: `${ROOTS.DASHBOARD}/organisation`,
            register: `${ROOTS.DASHBOARD}/organisation/registrera`,
            ansokan: `${ROOTS.DASHBOARD}/organisation/ansokan`,
            admin: `${ROOTS.DASHBOARD}/organisation/admin`,
            mina: `${ROOTS.DASHBOARD}/organisation/mina`,
            anvandare: `${ROOTS.DASHBOARD}/organisation/anvandare`,
            installningar: `${ROOTS.DASHBOARD}/organisation/installningar`,
            stadgar: `${ROOTS.DASHBOARD}/organisation/stadgar`,
            // Grunduppgifterna ur årsredovisningen. Nyckeln behåller å/ä/ö, pathen är ren ASCII.
            grunddata: `${ROOTS.DASHBOARD}/organisation/grunddata`,
            detalj: (id: string) => `${ROOTS.DASHBOARD}/organisation/${id}`,
        },
        admin: {
            root: `${ROOTS.DASHBOARD}/admin`,
            roller: {
                root: `${ROOTS.DASHBOARD}/admin/roller`,
                edit: (rollNamn: string) => `${ROOTS.DASHBOARD}/admin/roller/${rollNamn}`,
            },
            database: `${ROOTS.DASHBOARD}/admin/database`,
        },
        superadmin: {
            root: `${ROOTS.DASHBOARD}/superadmin`,
            mock: `${ROOTS.DASHBOARD}/superadmin/mock`,
            anvandarroller: `${ROOTS.DASHBOARD}/superadmin/anvandarroller`,
            organisationer: `${ROOTS.DASHBOARD}/superadmin/organisationer`,
            fel: `${ROOTS.DASHBOARD}/superadmin/fel`,
            rateLimiting: `${ROOTS.DASHBOARD}/superadmin/rate-limiting`,
        },
        arborist: {
            root: `${ROOTS.DASHBOARD}/arborist`,
            tradvardsplaner: `${ROOTS.DASHBOARD}/arborist/tradvardsplaner`,
            edit: (id: string) => `${ROOTS.DASHBOARD}/arborist/tradvardsplaner/edit/${id}`,
        },
        offert: {
            root: `${ROOTS.OFFERT}`,
            trädgårdsoffert: `${ROOTS.OFFERT}/arborist/tradvard`,
            trädgårdsfaktura: `${ROOTS.OFFERT}/tradgardsfaktura`,
        },
        dokument: {
            root: `${ROOTS.DASHBOARD}/dokument`,
            mapp: (id: string) => `${ROOTS.DASHBOARD}/dokument/mapp/${id}`,
        },
        arboristPool: {
            root: `${ROOTS.DASHBOARD}/arborist-pool`,
        },
        karta: {
            root: `${ROOTS.DASHBOARD}/karta`,
            senaste: `${ROOTS.DASHBOARD}/karta/senaste`,
            platser: `${ROOTS.DASHBOARD}/karta/platser`,
            hem: `${ROOTS.DASHBOARD}/karta/hem`,
        },
        kunder: {
            root: `${ROOTS.DASHBOARD}/kunder`,
            detalj: (id: string) => `${ROOTS.DASHBOARD}/kunder/${id}`,
        },
        medlemmar: {
            root: `${ROOTS.DASHBOARD}/medlemmar`,
            importera: `${ROOTS.DASHBOARD}/medlemmar/importera`,
            v2: `${ROOTS.DASHBOARD}/medlemmar/v2`,
            // Hushållets detaljvy. Nyckeln är hushållsnyckeln (adresshashen) och inte registrets
            // rad-id: rad-id:t nymintas vid varje sparning och import, så ett bokmärke hade dött
            // första gången någon rörde registret.
            hushåll: (nyckel: string) => `${ROOTS.DASHBOARD}/medlemmar/v2/${nyckel}`,
            // URL-segmentet är ASCII, nyckeln behåller sitt svenska ord.
            avgifter: `${ROOTS.DASHBOARD}/medlemmar/avgifter`,
            månadsfakturor: `${ROOTS.DASHBOARD}/medlemmar/manadsfakturor`,
        },
        fastighet: {
            root: `${ROOTS.DASHBOARD}/fastighet`,
            minaHem: `${ROOTS.DASHBOARD}/fastighet/mina-hem`,
            // Syskonnyckel och inte ett nästlat objekt under minaHem — det hade spräckt varje
            // befintlig referens till paths.dashboard.fastighet.minaHem. Nyckeln behåller ö,
            // URL-segmentet är ASCII.
            planlösning: `${ROOTS.DASHBOARD}/fastighet/mina-hem/planlosning`,
            planlösningV2: `${ROOTS.DASHBOARD}/fastighet/mina-hem/planlosning-v2`,
            // Bygglovshandlingar för hemmet. Syskonnyckel av samma skäl som planlösningen;
            // nyckeln behåller sitt svenska ord, URL-segmentet är ASCII.
            dokumentation: `${ROOTS.DASHBOARD}/fastighet/mina-hem/dokumentation`,
            // Närmaste hållplatser och avgångar. Syskonnyckel av samma skäl som planlösningen.
            kommunikationer: `${ROOTS.DASHBOARD}/fastighet/mina-hem/kommunikationer`,
            // Demosida för React Three Fiber. Syskonnyckel av samma skäl som ovan; nyckeln är
            // ren ASCII eftersom termen är ett bibliotek och inte ett svenskt ord.
            demoReactThreeFiber: `${ROOTS.DASHBOARD}/fastighet/mina-hem/demo-react-three-fiber`,
            // Bygglovsguiden: ansökan eller anmälan för en åtgärd på hemmet. Skild från
            // `dokumentation`, som är kommunens gamla ärenden. Nyckeln behåller ö, segmentet är ASCII.
            bygglov: `${ROOTS.DASHBOARD}/fastighet/mina-hem/bygglov`,
            bygglovsansökan: (id: string) => `${ROOTS.DASHBOARD}/fastighet/mina-hem/bygglov/${id}`,
            // Hus 3D: den AI-genererade 3D-modellen (Skapa) och fotona den förbättras med (Foton).
            skapa: `${ROOTS.DASHBOARD}/fastighet/mina-hem/skapa`,
            foton: `${ROOTS.DASHBOARD}/fastighet/mina-hem/foton`,
            värme: `${ROOTS.DASHBOARD}/fastighet/varme`,
            // Individuell mätning och debitering. Nyckeln är förkortningen, segmentet ASCII.
            imd: `${ROOTS.DASHBOARD}/fastighet/imd`,
            el: `${ROOTS.DASHBOARD}/fastighet/el`,
            värmepump: `${ROOTS.DASHBOARD}/fastighet/varmepump`,
            ritning: {
                root: `${ROOTS.DASHBOARD}/fastighet/3d-ritning`,
                ny: `${ROOTS.DASHBOARD}/fastighet/3d-ritning/ny`,
                detalj: (id: string) => `${ROOTS.DASHBOARD}/fastighet/3d-ritning/${id}`,
            },
            byggnader: {
                root: `${ROOTS.DASHBOARD}/fastighet/byggnader`,
                ny: `${ROOTS.DASHBOARD}/fastighet/byggnader/ny`,
                detalj: (id: string) => `${ROOTS.DASHBOARD}/fastighet/byggnader/${id}`,
            },
            // Fastigheter man äger eller är delägare i. Skilt från `minaHem`, som är
            // föreningens bild av var man bor — här är det ägandet som räknas.
            minaFastigheter: `${ROOTS.DASHBOARD}/fastighet/mina-fastigheter`,
            fastighet: (id: string) => `${ROOTS.DASHBOARD}/fastighet/mina-fastigheter/${id}`,
            hantverkare: `${ROOTS.DASHBOARD}/fastighet/hantverkare`,
            // OBS: inte `paths.dashboard.offert` — den är arboristens trädgårdsoffert.
            offerter: {
                root: `${ROOTS.DASHBOARD}/fastighet/offerter`,
                ny: `${ROOTS.DASHBOARD}/fastighet/offerter/ny`,
                detalj: (id: string) => `${ROOTS.DASHBOARD}/fastighet/offerter/${id}`,
                plats: (id: string, platsId: string) =>
                    `${ROOTS.DASHBOARD}/fastighet/offerter/${id}/plats/${platsId}`,
                dokumentation: (id: string) =>
                    `${ROOTS.DASHBOARD}/fastighet/offerter/${id}/dokumentation`,
            },
            energideklaration: `${ROOTS.DASHBOARD}/fastighet/energideklaration`,
        },
        backup: {
            root: `${ROOTS.DASHBOARD}/backup`,
        },
        aktieutdelning: {
            root: `${ROOTS.EKONOMI}/aktieutdelning`,
        },
    },
};
